import {Box} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react'
import ReactPlayerLoader from '@brightcove/react-player-loader'
import Image from '../image'
import useOcapiSite from '../../../../hooks/useOcapiSite'
import useVideoAutoplay from '../../../hooks/useVideoAutoplay'
import useIntersectionObserver from '../../../../hooks/use-intersection-observer'
import {hasValidImageSrc, hasValidString} from '../../../utils/utils'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

/**
 * BrightcoveVideo component for rendering Brightcove video player
 * Parent component controls play/pause via ref
 * @component
 * @param {Object} props - Component properties
 * @param {string} props.brightcoveId - Brightcove video ID
 * @param {Object} [props.poster] - Poster image object (see Image component) - rendered SSR until player loads
 * @param {string} [props.poster.src] - Image source URL (automatically converted to DIS URL if applicable)
 * @param {string} [props.poster.disBaseLink] - Optional DIS base link for responsive images (auto-generated from src if not provided)
 * @param {string} [props.poster.alt] - Alt text for accessibility
 * @param {string} [props.poster.title] - Image title
 * @param {boolean} [props.autoplay=false] - Whether to autoplay the video (will be muted)
 * @param {boolean} [props.videoControls=false] - Whether to show Brightcove player controls
 * @param {('eager'|'lazy')} [props.loading='lazy'] - Loading strategy for poster image
 * @param {string} [props.title] - Video title
 */
const BrightcoveVideo = forwardRef(
    (
        {
            brightcoveId,
            poster,
            autoplay = false,
            videoControls = false,
            loading = 'lazy',
            title,
            objectFit = 'contain',
            widths
        },
        ref
    ) => {
        const playerRef = useRef(null)
        const [playerReady, setPlayerReady] = useState(false)
        const {accountId, assemblyVideoPlayerId} = useOcapiSite()
        const shouldAutoplay = useVideoAutoplay(autoplay)
        const [setIntersectionRef, isInView] = useIntersectionObserver({useOnce: true})

        // Expose play/pause methods to parent
        useImperativeHandle(ref, () => ({
            play: () => {
                playerRef.current?.player_?.play()
            },
            pause: () => {
                playerRef.current?.player_?.pause()
            }
        }))

        if (!hasValidString(brightcoveId)) {
            console.warn('BrightcoveVideo: brightcoveId is required')
            return null
        }

        if (!accountId) {
            console.warn('BrightcoveVideo: accountId not available from site preferences')
            return null
        }

        if (!assemblyVideoPlayerId) {
            console.warn(
                'BrightcoveVideo: assemblyVideoPlayerId not available from site preferences'
            )
            return null
        }

        const onSuccess = ({ref: videoRef}) => {
            playerRef.current = videoRef
            const {player_} = videoRef

            // Set player styles
            player_.el().style.width = '100%'
            player_.el().style.height = '100%'

            // #202: Unnecessary ARIA attributes can cause unexpected behavior for screen reader users.
            // remove aria-live from the play button label
            player_.bigPlayButton?.controlTextEl_?.removeAttribute('aria-live')

            // Force controls visibility based on videoControls prop
            if (videoControls) {
                player_.controls(true)
            } else {
                player_.controls(false)
            }

            // Hide SSR poster when player is ready
            setPlayerReady(true)
        }

        return (
            <Box
                ref={setIntersectionRef}
                position="relative"
                width="100%"
                height="100%"
                data-testid="brightcove-video"
                css={{
                    '& .brightcove-react-player-loader>*': {
                        width: '100%',
                        height: '100%'
                    },
                    '& .vjs-tech': {objectFit}
                }}
            >
                {/* SSR poster image - shown until player is ready */}
                {hasValidImageSrc(poster) && !playerReady && (
                    <Box
                        position="absolute"
                        top="0"
                        left="0"
                        width="100%"
                        height="100%"
                        aria-hidden="true"
                    >
                        <Image
                            {...poster}
                            widths={widths}
                            objectFit={objectFit}
                            width="100%"
                            height="100%"
                            borderRadius="0"
                            loading={loading}
                            preload={loading === 'eager'}
                        />
                    </Box>
                )}

                {isInView && (
                    <ReactPlayerLoader
                        attrs={{
                            style: {
                                width: '100%',
                                height: '100%'
                            }
                        }}
                        props={{
                            style: {
                                height: '100%'
                            }
                        }}
                        accountId={accountId}
                        playerId={assemblyVideoPlayerId}
                        videoId={brightcoveId}
                        options={{
                            autoplay: shouldAutoplay,
                            muted: shouldAutoplay
                        }}
                        // disable brightcove poster when we have a poster as it is not responsive and is downloading the maximum size image
                        {...(hasValidImageSrc(poster) && {
                            poster: getAssetUrl('static/img/transparent.png')
                        })}
                        embedOptions={{
                            responsive: true
                        }}
                        onSuccess={onSuccess}
                        aria-label={title}
                    />
                )}
            </Box>
        )
    }
)

if (process.env.NODE_ENV === 'development') {
    BrightcoveVideo.displayName = 'BrightcoveVideo'
}

BrightcoveVideo.propTypes = {
    brightcoveId: PropTypes.string,
    poster: PropTypes.object,
    autoplay: PropTypes.bool,
    videoControls: PropTypes.bool,
    loading: PropTypes.oneOf(['eager', 'lazy']),
    title: PropTypes.string,
    objectFit: PropTypes.string,
    widths: PropTypes.object
}

export default BrightcoveVideo
